
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Trash2, ExternalLink } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import type { SitemapEntry } from '@/types/sitemap';

interface SitemapEntriesTableProps {
  entries: SitemapEntry[];
  isLoading: boolean;
  onToggleEntry: (id: string, isActive: boolean) => void;
  onDeleteEntry: (id: string) => void;
}

export const SitemapEntriesTable: React.FC<SitemapEntriesTableProps> = ({
  entries,
  isLoading,
  onToggleEntry,
  onDeleteEntry,
}) => {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center space-x-4">
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-6 w-12" />
          </div>
        ))}
      </div>
    );
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'static':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'dynamic':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'generated':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getFrequencyColor = (frequency: string) => {
    switch (frequency) {
      case 'always':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'hourly':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'daily':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'weekly':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'monthly':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'yearly':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'never':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-gray-900 dark:text-gray-100">URL</TableHead>
            <TableHead className="text-gray-900 dark:text-gray-100">Type</TableHead>
            <TableHead className="text-gray-900 dark:text-gray-100">Priority</TableHead>
            <TableHead className="text-gray-900 dark:text-gray-100">Change Frequency</TableHead>
            <TableHead className="text-gray-900 dark:text-gray-100">Active</TableHead>
            <TableHead className="text-gray-900 dark:text-gray-100">Last Modified</TableHead>
            <TableHead className="text-gray-900 dark:text-gray-100">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-gray-600 dark:text-gray-400">
                No sitemap entries found
              </TableCell>
            </TableRow>
          ) : (
            entries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className="font-medium text-gray-900 dark:text-gray-100">
                  <div className="flex items-center gap-2">
                    <span className="truncate max-w-xs">{entry.url}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(window.location.origin + entry.url, '_blank')}
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={getTypeColor(entry.entry_type)}>
                    {entry.entry_type}
                  </Badge>
                </TableCell>
                <TableCell className="text-gray-900 dark:text-gray-100">{entry.priority}</TableCell>
                <TableCell>
                  <Badge className={getFrequencyColor(entry.change_frequency)}>
                    {entry.change_frequency}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Switch
                    checked={entry.is_active}
                    onCheckedChange={(checked) => onToggleEntry(entry.id, checked)}
                  />
                </TableCell>
                <TableCell className="text-sm text-gray-600 dark:text-gray-400">
                  {entry.last_modified 
                    ? new Date(entry.last_modified).toLocaleDateString()
                    : new Date(entry.updated_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteEntry(entry.id)}
                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
